import { all, put, select, takeLatest } from "redux-saga/effects";
import axios from "axios";
import { toast } from "react-toastify";

import {
  selectAuthToken,
  selectOrganisationId,
  selectSupplierId,
} from "../selectors/login.selector";
import * as API from "../utils/api";
import {
  getWipItems,
  setWipItems,
  setWipStatusRollup,
  uploadWipExcel,
  setUploadWipExcelSuccess,
  setUploadWipExcelError,
  getWipTrackers,
  setWipTrackers,
  getWipTrackerDetails,
  setWipTrackerDetails,
  bulkUpdateWipDetails,
  setBulkUpdateWipDetailsSuccess,
  setBulkUpdateWipDetailsError,
  getWipComments,
  setWipComments,
  addWipComment,
  setAddWipCommentSuccess,
  resolveWipComment,
  setResolveWipCommentSuccess,
  deleteWipTracker,
  setDeleteWipTrackerSuccess,
  // WIP Reports actions (replaces batch update IDs)
  getWipReports,
  setWipReports,
  getWipReportById,
  setWipReportById,
  setSelectedReportId,
  setLockedState,
  // Batch update actions (backward compatibility)
  getBatchUpdateIds,
  setBatchUpdateIds,
  getWipItemsByBatch,
  setWipItemsByBatch,
  setBatchError,
  // Past-due actions
  getPastDueItems,
  setPastDueItems,
  setPastDueError,
} from "../reducers/wip.reducer";
import { logout } from "../reducers/login.reducer";
import { setFilesUploadedIds } from "../reducers/misc.reducer";
import {
  selectSelectedBatchId,
  selectSelectedReportId,
  selectBatchItems,
  selectWipTrackerDetails,
} from "../selectors/wip.selector";

/**
 * Flatten substages array into flat row fields
 * Uses displayName from backend substage object
 */
function flattenSubstagesArray(stage, flatRow) {
  if (
    !stage.substages ||
    !Array.isArray(stage.substages) ||
    stage.substages.length === 0
  ) {
    return;
  }

  const stageKeyForField = stage.stageKey || stage._id || "unknown_stage";

  stage.substages.forEach((substage) => {
    const baseFieldName = `${stageKeyForField}: ${substage.substageKey}`;

    // Store displayName from backend for UI headers
    if (substage.displayName) {
      flatRow[`${baseFieldName} (DISPLAY_NAME)`] = substage.displayName;
    }

    // Standard fields - use lowercase backend field names
    if (substage.req !== undefined) {
      flatRow[`${baseFieldName} (REQ)`] = substage.req || null;
    }
    if (substage.actual !== undefined) {
      flatRow[`${baseFieldName} (ACTUAL)`] = substage.actual || null;
    }
    if (substage.control !== undefined && substage.control !== null) {
      flatRow[`${baseFieldName} (CONTROL)`] = substage.control;
    }
    if (substage.controlII !== undefined && substage.controlII !== null) {
      flatRow[`${baseFieldName} (CONTROL II)`] = substage.controlII;
    }
    if (substage.days !== undefined && substage.days !== null) {
      flatRow[`${baseFieldName} (DAYS)`] = substage.days;
    }
    if (substage.status !== undefined) {
      flatRow[`${baseFieldName} (STATUS)`] = substage.status || "";
    }
    if (substage.approvalDate !== undefined) {
      flatRow[`${baseFieldName} (APPROVAL DATE)`] =
        substage.approvalDate || null;
    }
    if (substage.comments !== undefined) {
      flatRow[`${baseFieldName} (COMMENT)`] = Array.isArray(substage.comments)
        ? substage.comments
        : [];
    }

    // Store substage ID for updates
    if (substage._id) {
      flatRow[`${baseFieldName} (_id)`] = substage._id;
    }

    // Additional fields (if any)
    if (
      substage.additionalFields &&
      Object.keys(substage.additionalFields).length > 0
    ) {
      Object.entries(substage.additionalFields).forEach(([key, value]) => {
        flatRow[`${baseFieldName} (${key.toUpperCase()})`] = value;
      });
    }
  });
}

/**
 * Flatten stepsInStage object for simple stages (main_information, tc_certificate, material_specification)
 * These are simple key-value pairs, not nested substage structures
 */
function flattenStepsInStage(stage, flatRow) {
  if (!stage.stepsInStage || Object.keys(stage.stepsInStage).length === 0) {
    return;
  }

  const stageKeyForField = stage.stageKey || stage._id || "unknown_stage";

  // For simple stages, store fields with stage prefix to avoid conflicts
  Object.entries(stage.stepsInStage).forEach(([key, value]) => {
    // Store as: "stageKey_key" (e.g., "main_information_CATEGORY")
    flatRow[`${stageKeyForField}_${key}`] = value;
  });
}

/**
 * Map frontend field type to backend field name
 */
function mapFieldTypeToBackend(fieldType) {
  const mapping = {
    REQ: "req",
    ACTUAL: "actual",
    CONTROL: "control",
    "CONTROL II": "controlII",
    DAYS: "days",
    STATUS: "status",
    "APPROVAL DATE": "approvalDate",
    COMMENT: "comments",
    COMMENTS: "comments",
  };

  return mapping[fieldType] || fieldType.toLowerCase();
}

/**
 * Maps substage keys to column field names (DEPRECATED - kept for reference)
 * Per WIP Frontend API Migration Guide (Ground-up implementation - no legacy)
 */
function getSubstageFieldMapping(stageKey, substageKey) {
  const mappings = {
    // Main Information Stage (also uses substages in ground-up implementation)
    main_information: {
      basic_info: {
        // These are stored as substage fields in the new structure
        factoryField: "Factory",
        vendorStyleField: "VENDOR STYLE #",
        poField: "PO #",
        colourField: "COLOUR",
        customerField: "CUSTOMER (Select from drop down)",
        unitsField: "UNITS",
        descriptionField: "DESCRIPTION",
      },
    },

    // Fabric Approval Stage
    fabric_approval: {
      lab_dips_strike_off_knit_down: {
        reqField: "(REQ) LAB DIPS/STRIKE OFF/ KNIT DOWN APP DATE",
        actualField: "(ACTUAL) LAB DIPS/STRIKE OFF/ KNIT DOWN APP DATE",
        statusField: "(ACTUAL) LAB DIPS/STRIKE OFF/ KNIT DOWN APP DATE Status",
        commentField:
          "(ACTUAL) LAB DIPS/STRIKE OFF/ KNIT DOWN APP DATE Comment",
      },
      bulk_fabric: {
        reqField: "(REQ) BULK FABRIC APP DATE",
        actualField: "(ACTUAL) BULK FABRIC APP DATE",
        statusField: "(ACTUAL) BULK FABRIC APP DATE Status",
        commentField: "(ACTUAL) BULK FABRIC APP DATE Comment",
      },
    },

    // Trim Approval Stage (matching actual backend keys)
    trim_approval: {
      trim_smpl: {
        reqField: "(REQ) TRIM SMPL APPROVAL DATE",
        actualField: "(ACTUAL) TRIM SMPL APPROVAL DATE",
        statusField: "(ACTUAL) TRIM SMPL APPROVAL DATE Status",
        commentField: "(ACTUAL) TRIM SMPL APPROVAL DATE Comment",
      },
      bulk_trim: {
        reqField: "(REQ) BULK TRIM APPROVAL DATE",
        actualField: "(ACTUAL) BULK TRIM APPROVAL DATE",
        statusField: "(ACTUAL) BULK TRIM APPROVAL DATE Status",
        commentField: "(ACTUAL) BULK TRIM APPROVAL DATE Comment",
      },
    },

    // Sample Status Stage (matching actual backend keys)
    sample_status: {
      fit: {
        reqField: "(REQ) FIT SUBMIT ETA DATE",
        actualField: "(ACTUAL) FIT APPROVAL DATE",
        statusField: "(ACTUAL) FIT APPROVAL DATE Status",
        commentField: "(ACTUAL) FIT APPROVAL DATE Comment",
      },
      pp: {
        reqField: "(REQ) PP APPROVAL DATE",
        actualField: "(ACTUAL) PP APPROVED DATE",
        statusField: "(ACTUAL) PP APPROVED DATE Status",
        commentField: "(ACTUAL) PP APPROVED DATE Comment",
      },
      top_arrival_deadline: {
        reqField: "TOP ARRIVAL DEADLINE",
        actualField: "(ACTUAL) TOP ARRIVAL DEADLINE",
        statusField: "(ACTUAL) TOP APPROVAL DATE Status",
        commentField: "(ACTUAL) TOP APPROVAL DATE Comment",
      },
      marketing_top_arrival_deadline: {
        reqField: "MARKETING TOP ARRIVAL DEADLINE",
        actualField: "(ACTUAL) MARKETING TOP ARRIVAL DEADLINE",
      },
    },

    // Testing Stage (matching actual backend keys)
    testing: {
      fabric_testing_date: {
        reqField: "(REQ) SCHEDULED FABRIC TESTING DATE",
        actualField: "(ACTUAL) FABRIC TESTING DATE",
        statusField: "FABRIC TEST CONCLUSION",
        commentField: "REASONS FOR FAILURE AND RETEST",
      },
      garment_testing_date: {
        reqField: "(REQ) SCHEDULED GARMENT TESTING DATE",
        actualField: "(ACTUAL) GARMENT TESTING DATE",
        statusField: "GARMENT TEST CONCLUSION",
        commentField: "REASONS FOR FAILURE AND RETEST",
      },
    },

    // Production Stage (matching actual backend keys)
    production: {
      cut_date: {
        reqField: "(REQ) PLAN CUT DATE",
        actualField: "(ACTUAL) CUT DATE",
      },
      sewing: {
        actualField: "(ACTUAL) SEWING START",
        // Note: SEWING END is separate
      },
    },

    // Inspection Stage (matching actual backend keys)
    inspection: {
      "3rd_party_final_inspection_date": {
        reqField: "(REQ) 3RD PARTY FINAL INSPECTION REQUIRED DATE",
        actualField: "(ACTUAL) 3RD PARTY FINAL INSPECTION ACTUAL DATE",
        statusField: "Inspection Approval Status",
        commentField: "Inspection Approval Comment",
      },
    },

    // Logistics Stage (matching actual backend keys)
    logistics: {
      date_to_book_the_vessel: {
        reqField: "LOGISTICS: DATE TO BOOK THE VESSEL (REQ)",
        actualField: "LOGISTICS: DATE TO BOOK THE VESSEL (ACTUAL)",
        controlField: "LOGISTICS: DATE TO BOOK THE VESSEL (CONTROL)",
        controlIIField: "LOGISTICS: DATE TO BOOK THE VESSEL (CONTROL II)",
        daysField: "LOGISTICS: DATE TO BOOK THE VESSEL (DAYS)",
        statusField: "LOGISTICS: DATE TO BOOK THE VESSEL (STATUS)",
        approvalDateField: "LOGISTICS: DATE TO BOOK THE VESSEL (APPROVAL DATE)",
        commentField: "LOGISTICS: DATE TO BOOK THE VESSEL (COMMENT)",
      },
      cargo_hand_over: {
        reqField: "LOGISTICS: CARGO HAND OVER (REQ)",
        actualField: "LOGISTICS: CARGO HAND OVER (ACTUAL)",
        controlField: "LOGISTICS: CARGO HAND OVER (CONTROL)",
        controlIIField: "LOGISTICS: CARGO HAND OVER (CONTROL II)",
        daysField: "LOGISTICS: CARGO HAND OVER (DAYS)",
        statusField: "LOGISTICS: CARGO HAND OVER (STATUS)",
        approvalDateField: "LOGISTICS: CARGO HAND OVER (APPROVAL DATE)",
        commentField: "LOGISTICS: CARGO HAND OVER (COMMENT)",
      },
      on_board_end: {
        reqField: "LOGISTICS: ON BOARD END (REQ)",
        actualField: "LOGISTICS: ON BOARD END (ACTUAL)",
        controlField: "LOGISTICS: ON BOARD END (CONTROL)",
        controlIIField: "LOGISTICS: ON BOARD END (CONTROL II)",
        daysField: "LOGISTICS: ON BOARD END (DAYS)",
        statusField: "LOGISTICS: ON BOARD END (STATUS)",
        approvalDateField: "LOGISTICS: ON BOARD END (APPROVAL DATE)",
        commentField: "LOGISTICS: ON BOARD END (COMMENT)",
      },
      in_dc_end_cancel_date: {
        reqField: "LOGISTICS: IN DC END Cancel Date (REQ)",
        actualField: "LOGISTICS: IN DC END Cancel Date (ACTUAL)",
        controlField: "LOGISTICS: IN DC END Cancel Date (CONTROL)",
        controlIIField: "LOGISTICS: IN DC END Cancel Date (CONTROL II)",
        daysField: "LOGISTICS: IN DC END Cancel Date (DAYS)",
        statusField: "LOGISTICS: IN DC END Cancel Date (STATUS)",
        approvalDateField: "LOGISTICS: IN DC END Cancel Date (APPROVAL DATE)",
        commentField: "LOGISTICS: IN DC END Cancel Date (COMMENT)",
      },
    },
  };

  return mappings[stageKey]?.[substageKey] || null;
}

/**
 * Parse field name to extract substage and field information
 * Format: "{STAGE_KEY}: {SUBSTAGE_KEY} ({FIELD})"
 * Example: "fabric_approval: lab_dips_submit (REQ)"
 */
function parseFieldName(fieldName) {
  // Check if this is a nested substage field (contains ":" and ends with ")")
  if (fieldName.includes(":") && fieldName.endsWith(")")) {
    // Extract parts: "STAGE: SUBSTAGE (FIELD)"
    const colonIndex = fieldName.indexOf(":");
    const lastOpenParen = fieldName.lastIndexOf("(");
    const lastCloseParen = fieldName.lastIndexOf(")");

    if (
      colonIndex > 0 &&
      lastOpenParen > colonIndex &&
      lastCloseParen > lastOpenParen
    ) {
      const stageKey = fieldName.substring(0, colonIndex).trim();
      const substageKey = fieldName
        .substring(colonIndex + 1, lastOpenParen)
        .trim();
      const field = fieldName
        .substring(lastOpenParen + 1, lastCloseParen)
        .trim();

      // Handle substage ID field: "fabric_approval: lab_dips_submit (_id)"
      if (field === "_id") {
        return {
          isNested: true,
          stageKey,
          substageKey,
          field: "_id",
          isIdField: true,
        };
      }

      // Handle display name field: "fabric_approval: lab_dips_submit (DISPLAY_NAME)"
      if (field === "DISPLAY_NAME") {
        return {
          isNested: true,
          stageKey,
          substageKey,
          field: "DISPLAY_NAME",
          isDisplayNameField: true,
        };
      }

      return {
        isNested: true,
        stageKey,
        substageKey,
        field,
      };
    }
  }

  // Simple field - format: "stageKey_fieldName" (e.g., "main_information_CATEGORY")
  return {
    isNested: false,
    stageKey: null,
    substageKey: null,
    field: null,
  };
}

function* getWipItemsWorker(action) {
  const authToken = yield select(selectAuthToken);
  const organisationId = yield select(selectOrganisationId);
  const { season, limit, offset } = action.payload || {};

  if (!organisationId) {
    console.warn("No Organisation Id found, abandoning API call");
    return;
  }

  const requestURL = API.WIP.GET_WIP_ITEMS.replace(
    "<ORGANISATION_ID>",
    organisationId
  );

  const headers = {
    "Content-Type": "application/json",
    Authorization: authToken,
  };

  const params = {};
  if (season) params.season = season;
  if (limit) params.limit = limit;
  if (offset) params.offset = offset;

  try {
    const response = yield axios.get(requestURL, { headers, params });

    if (response && response.status === 200 && response.data) {
      if (response.data.success) {
        const wipItems = response.data.results.wipItems || [];

        yield put(setWipItems(wipItems));

        // Also set as trackers for the new tracker view
        // Transform wipItems to tracker format for compatibility
        const trackers = wipItems.map((item) => ({
          _id: item._id,
          id: item._id, // DataGrid requires 'id' field
          vendor: item.supplierName,
          season: item.season,
          department: "", // Not in current data structure
          year: item.season ? item.season.split(",")[1]?.trim() : "",
          uploadedBy: { name: "System" }, // Not in current data structure
          uploadedAt: item.createdAt,
          status: "active",
          // Include factoryId for Excel-style view navigation
          factoryId: item.factoryId,
          factoryName: item.factoryName,
        }));

        yield put(
          setWipTrackers({
            trackers,
            pagination: response.data.results.pagination || {},
          })
        );

        // Calculate overall status rollup from all items
        const overallStatusRollup = wipItems.reduce(
          (acc, item) => {
            const itemRollup = item.statusRollup || {};
            return {
              onTrackCount: acc.onTrackCount + (itemRollup.onTrackCount || 0),
              atRiskCount: acc.atRiskCount + (itemRollup.atRiskCount || 0),
              overdueCount: acc.overdueCount + (itemRollup.overdueCount || 0),
              completionPercent:
                acc.completionPercent + (itemRollup.completionPercent || 0),
            };
          },
          {
            onTrackCount: 0,
            atRiskCount: 0,
            overdueCount: 0,
            completionPercent: 0,
          }
        );

        // Calculate average completion percentage
        if (wipItems.length > 0) {
          overallStatusRollup.completionPercent = Math.round(
            overallStatusRollup.completionPercent / wipItems.length
          );
        }

        yield put(setWipStatusRollup(overallStatusRollup));
      }
    }
  } catch (error) {
    console.error("Failed to fetch WIP items:", error);
    toast.error("Failed to load WIP items");
    if (error && error.response && error.response.status === 401) {
      yield put(logout());
    }
  }
}

function* uploadWipExcelWorker(action) {
  const authToken = yield select(selectAuthToken);
  const organisationId = yield select(selectOrganisationId);
  const { file, season } = action.payload;

  if (!organisationId) {
    console.warn("No Organisation Id found, abandoning API call");
    yield put(setUploadWipExcelError("Organisation ID not found"));
    return;
  }

  if (!season) {
    console.warn("Season is required for WIP import");
    yield put(setUploadWipExcelError("Season is required"));
    toast.error("Season is required for import");
    return;
  }

  const requestURL = API.WIP.IMPORT;

  const headers = {
    Authorization: authToken,
  };

  try {
    const formData = new FormData();

    // Handle both single file and array of files
    const fileToUpload = Array.isArray(file)
      ? file[0].file || file[0]
      : file.file || file;

    formData.append("file", fileToUpload);
    formData.append("organisationId", organisationId);
    formData.append("season", season);

    const response = yield axios.post(requestURL, formData, { headers });

    if (response && response.status === 200 && response.data) {
      if (response.data.success) {
        yield put(setUploadWipExcelSuccess(response.data.results));
        toast.success(
          `WIP import successful! ${response.data.results.summary.inserted} items imported`
        );

        // Mark files as uploaded (for compatibility with file upload component)
        const uploadedFiles = {};
        if (Array.isArray(file)) {
          file.forEach((f) => {
            uploadedFiles[f.id] = true;
          });
        } else {
          uploadedFiles[file.id] = true;
        }
        yield put(setFilesUploadedIds(uploadedFiles));

        // Reload WIP items after successful upload
        yield put(getWipItems({ season, limit: 10000, offset: 0 }));
      } else {
        yield put(
          setUploadWipExcelError(response.data.message || "Upload failed")
        );
        toast.error(response.data.message || "Upload failed");
      }
    }
  } catch (error) {
    console.error("Failed to upload WIP Excel:", error);
    const errorMessage =
      error.response?.data?.message || error.message || "Upload failed";
    yield put(setUploadWipExcelError(errorMessage));
    toast.error(errorMessage);
    if (error && error.response && error.response.status === 401) {
      yield put(logout());
    }
  }
}

function* getWipTrackersWorker(action) {
  // Trackers are just wipItems in the actual backend
  // Delegate to getWipItemsWorker
  yield* getWipItemsWorker(action);
}

function* getWipTrackerDetailsWorker(action) {
  const authToken = yield select(selectAuthToken);
  const organisationId = yield select(selectOrganisationId);
  const {
    trackerId,
    season,
    factoryId,
    supplierId,
    batchUpdateDate,
    batchUpdateId,
  } = action.payload;

  if (!organisationId) {
    console.warn("Missing organisation ID, abandoning API call");
    return;
  }

  // If trackerId is provided, fetch single item (old behavior for compatibility)
  // Otherwise, fetch all items for the season/factory (new Excel-style view)
  const isSingleItemView = !!trackerId;

  console.log("=== WIP Saga: getWipTrackerDetails ===");
  console.log("trackerId:", trackerId);
  console.log("season:", season);
  console.log("factoryId:", factoryId);
  console.log("supplierId:", supplierId);
  console.log("batchUpdateDate:", batchUpdateDate);
  console.log("batchUpdateId:", batchUpdateId);
  console.log("isSingleItemView:", isSingleItemView);

  let requestURL;
  if (isSingleItemView) {
    requestURL = API.WIP.GET_WIP_ITEM.replace(
      "<ORGANISATION_ID>",
      organisationId
    ).replace("<WIP_ITEM_ID>", trackerId);
    console.log("Using single item endpoint:", requestURL);
  } else {
    // Fetch all wipItems for this organisation (Excel-style view)
    // Backend endpoint: GET /api/v1/wip/organisation/:orgId/items-detailed
    requestURL = API.WIP.GET_WIP_ITEMS_DETAILED.replace(
      "<ORGANISATION_ID>",
      organisationId
    );

    // Add query parameters for filtering (per Migration Guide)
    const queryParams = [];
    if (season) queryParams.push(`season=${encodeURIComponent(season)}`);
    if (factoryId) queryParams.push(`factoryId=${factoryId}`);
    if (supplierId) queryParams.push(`supplierId=${supplierId}`);
    if (batchUpdateDate)
      queryParams.push(
        `batchUpdateDate=${encodeURIComponent(batchUpdateDate)}`
      );
    if (batchUpdateId)
      queryParams.push(`batchUpdateId=${encodeURIComponent(batchUpdateId)}`);

    if (queryParams.length > 0) {
      requestURL += `?${queryParams.join("&")}`;
    }
    console.log("Using items-detailed endpoint:", requestURL);
  }

  const headers = {
    "Content-Type": "application/json",
    Authorization: authToken,
  };

  try {
    const response = yield axios.get(requestURL, { headers });
    console.log("GET response received:", response.status);

    if (response && response.status === 200 && response.data) {
      if (response.data.success) {
        // Handle both single item and multiple items response
        let wipItems = [];

        if (isSingleItemView) {
          // Single item response: data is the wipItem itself
          wipItems = [response.data.results];
          console.log("Single item loaded:", wipItems[0]?._id);
        } else {
          // Multiple items response: data should have wipItems array
          // Backend might return { data: { wipItems: [...] } } or { data: [...] }
          wipItems =
            response.data.results.wipItems || response.data.results || [];
          console.log("Multiple items loaded:", wipItems.length);
        }

        if (wipItems.length === 0) {
          console.warn("No WIP items in response");
          yield put(
            setWipTrackerDetails({
              details: [],
              tracker: null,
              allTrackers: [],
            })
          );
          return;
        }

        // Transform each wipItem into ONE row (Excel-style)
        // Flatten all stages into a single row object
        const details = wipItems
          .map((wipItem) => {
            if (!wipItem || !wipItem._id) {
              console.error("Invalid wipItem:", wipItem);
              return null;
            }

            const flatRow = {
              id: wipItem._id, // DataGrid requires 'id' field
              _id: wipItem._id,

              // Basic wipItem fields (handle object vs string for factoryId/supplierId)
              factoryName: wipItem.factoryId?.name || wipItem.factoryName,
              factory: wipItem.factoryId?.name || wipItem.factoryName, // Alias for column compatibility
              supplierName: wipItem.supplierId?.name || wipItem.supplierName,
              styleNumber: wipItem.styleNumber,
              vendorStyle: wipItem.styleNumber, // Alias for column compatibility
              rootsStyle: wipItem.styleNumber, // Alias for column compatibility
              po: wipItem.poNumber,
              poNumber: wipItem.poNumber,
              colour: wipItem.colour,
              customer: wipItem.customer,
              description: wipItem.description,
              units: wipItem.units,
              season: wipItem.season,
              category: wipItem.category || "", // From Excel
              department: wipItem.department || "",

              // Store statusRollup for row-level status indicators
              statusRollup: wipItem.statusRollup,
            };

            // Standard stage order: main_information first, then material, then tc, then others
            const STANDARD_STAGE_ORDER = [
              "main_information",
              "material_specification",
              "tc_certificate",
              "fabric_approval",
              "trim_approval",
              "sample_status",
              "testing",
              "production",
              "inspection",
              "logistics",
            ];

            // Flatten ALL stages into this single row
            // Process stages in STANDARD_STAGE_ORDER to maintain consistent column order
            if (wipItem.stages && Array.isArray(wipItem.stages)) {
              // Create a map of stages by stageKey for quick lookup
              const stagesMap = new Map();
              wipItem.stages.forEach((stage) => {
                stagesMap.set(stage.stageKey, stage);
              });

              // Process stages in STANDARD_STAGE_ORDER first
              STANDARD_STAGE_ORDER.forEach((stageKey) => {
                const stage = stagesMap.get(stageKey);
                if (stage) {
                  // Add stage metadata with prefixed keys to avoid conflicts
                  flatRow[`${stage.stageKey}_status`] = stage.status;
                  flatRow[`${stage.stageKey}_daysLate`] = stage.daysLate || 0;
                  flatRow[`${stage.stageKey}_displayName`] = stage.displayName;
                  flatRow[`${stage.stageKey}_id`] = stage._id; // Store stage ID for updates

                  // Backend structure: stages can have either substages array or stepsInStage object
                  // - substages array: for complex stages (fabric_approval, trim_approval, etc.)
                  // - stepsInStage object: for simple stages (main_information, tc_certificate, material_specification)
                  const hasSubstages =
                    stage.substages &&
                    Array.isArray(stage.substages) &&
                    stage.substages.length > 0;
                  const hasStepsInStage =
                    stage.stepsInStage &&
                    Object.keys(stage.stepsInStage).length > 0;

                  if (hasSubstages) {
                    // Complex stage with substages array
                    flattenSubstagesArray(stage, flatRow);
                  } else if (hasStepsInStage) {
                    // Simple stage with stepsInStage key-value pairs
                    flattenStepsInStage(stage, flatRow);
                  }

                  // Remove from map so we know it's been processed
                  stagesMap.delete(stageKey);
                }
              });

              // Process any remaining stages not in STANDARD_STAGE_ORDER (maintain backend order)
              stagesMap.forEach((stage) => {
                // Add stage metadata with prefixed keys to avoid conflicts
                flatRow[`${stage.stageKey}_status`] = stage.status;
                flatRow[`${stage.stageKey}_daysLate`] = stage.daysLate || 0;
                flatRow[`${stage.stageKey}_displayName`] = stage.displayName;
                flatRow[`${stage.stageKey}_id`] = stage._id; // Store stage ID for updates

                // Backend structure: stages can have either substages array or stepsInStage object
                const hasSubstages =
                  stage.substages &&
                  Array.isArray(stage.substages) &&
                  stage.substages.length > 0;
                const hasStepsInStage =
                  stage.stepsInStage &&
                  Object.keys(stage.stepsInStage).length > 0;

                if (hasSubstages) {
                  // Complex stage with substages array
                  flattenSubstagesArray(stage, flatRow);
                } else if (hasStepsInStage) {
                  // Simple stage with stepsInStage key-value pairs
                  flattenStepsInStage(stage, flatRow);
                }
              });
            }

            // Keep dates as ISO strings in Redux state (serializable)
            // DataGrid will handle date conversion via valueGetter

            // DEBUG: Log all field names created for this row
            console.log(
              "🔍 Row field names:",
              Object.keys(flatRow).filter((k) => k.includes("LOGISTICS"))
            );

            return flatRow;
          })
          .filter(Boolean); // Remove null entries

        console.log(
          `✅ Transformed ${wipItems.length} WIP items into ${details.length} flat rows for Excel-style view`
        );
        console.log("First row ID:", details[0]?.id);
        console.log(
          "First row keys count:",
          Object.keys(details[0] || {}).length
        );
        console.log(
          "🔍 Sample logistics fields from first row:",
          Object.keys(details[0] || {})
            .filter((k) => k.includes("LOGISTICS"))
            .slice(0, 10)
        );

        // Log comment fields to verify they're being transformed
        details.forEach((row, idx) => {
          const commentFields = Object.keys(row).filter((key) =>
            key.includes("Comment")
          );
          console.log(
            `Row ${idx} (${row.id}) has ${commentFields.length} comment fields`
          );

          // Special check for row 2 (the one we just edited)
          if (row.id === "6911a18a3fd3c94c3a0ace25") {
            console.log(`🔍 EDITED ROW DETAILS for ${row.id}:`);
            const labDipsComment =
              row["(ACTUAL) LAB DIPS/STRIKE OFF/ KNIT DOWN APP DATE Comment"];
            console.log("  Lab Dips Comment:", labDipsComment);
            console.log(
              "  Lab Dips Comment type:",
              Array.isArray(labDipsComment) ? "array" : typeof labDipsComment
            );
            console.log(
              "  Lab Dips Comment length:",
              Array.isArray(labDipsComment) ? labDipsComment.length : "N/A"
            );
            console.log(
              "  Lab Dips Status:",
              row["(ACTUAL) LAB DIPS/STRIKE OFF/ KNIT DOWN APP DATE Status"]
            );
            console.log(
              "  All comment values:",
              commentFields.map((f) => {
                const val = row[f];
                if (Array.isArray(val)) {
                  return `${f}=[${val.length} comments]`;
                }
                return `${f}=${val}`;
              })
            );
          }

          commentFields.forEach((field) => {
            const commentValue = row[field];
            if (commentValue) {
              if (Array.isArray(commentValue) && commentValue.length > 0) {
                console.log(
                  `  - ${field}: [${commentValue.length} comments]`,
                  commentValue
                );
              } else {
                console.log(`  - ${field}:`, commentValue);
              }
            }
          });
        });

        console.log("🚀 [Saga] About to dispatch setWipTrackerDetails");
        console.log("🚀 [Saga] details.length:", details.length);
        console.log(
          "🚀 [Saga] details[0] keys:",
          details[0] ? Object.keys(details[0]).length : 0
        );
        console.log("🚀 [Saga] details[0] sample:", details[0]);

        yield put(
          setWipTrackerDetails({
            details,
            tracker: wipItems[0], // Keep first item for compatibility
            allTrackers: wipItems, // Store all items for reference
          })
        );
      } else {
        console.error("Response success=false:", response.data);
        toast.error(
          "Failed to load data: " + (response.data.message || "Unknown error")
        );
      }
    } else {
      console.error("Invalid response:", response);
      toast.error("Invalid response from server");
    }
  } catch (error) {
    console.error("Failed to fetch WIP item details:", error);
    console.error("Error response:", error.response);
    toast.error("Failed to load WIP tracker details");
    if (error && error.response && error.response.status === 401) {
      yield put(logout());
    }
  }
}

function* bulkUpdateWipDetailsWorker(action) {
  const authToken = yield select(selectAuthToken);
  const organisationId = yield select(selectOrganisationId);
  const selectedReportId = yield select(selectSelectedReportId);
  const selectedBatchId = yield select(selectSelectedBatchId); // Backward compatibility
  const batchItems = yield select(selectBatchItems);
  const trackerDetails = yield select(selectWipTrackerDetails);
  const wipId = selectedReportId || selectedBatchId; // Use new format, fallback to old
  console.log("wipId (selectedReportId):", wipId);
  const { wipItemId, updates, viewParams, batchUpdates } = action.payload;

  console.log("=== bulkUpdateWipDetailsWorker ===");
  console.log("Action payload:", action.payload);

  // Check if this is a batch update
  const isBatchUpdate = Array.isArray(batchUpdates) && batchUpdates.length > 0;

  if (isBatchUpdate) {
    console.log("🔄 BATCH UPDATE MODE");
    console.log("Batch updates count:", batchUpdates.length);

    if (!organisationId) {
      console.warn("Missing organisation ID");
      yield put(setBulkUpdateWipDetailsError("Organisation ID required"));
      return;
    }

    // Use batch endpoint with batch ID
    const requestURL = API.WIP.UPDATE_WIP_ITEMS_BY_REPORT.replace(
      "<ORGANISATION_ID>",
      organisationId
    ).replace("<WIP_ID>", wipId);

    // Process each batch update item - parse field names like single item updates
    console.log("🔄 Converting batch updates to backend format...");
    const processedBatchUpdates = batchUpdates
      .map((batchItem, itemIdx) => {
        const { wipItemId, updates } = batchItem;

        // Defensive check - ensure updates exists and is an array
        if (!updates || !Array.isArray(updates)) {
          console.warn(
            `⚠️ Batch item ${itemIdx + 1} (${wipItemId}) has invalid updates:`,
            updates
          );
          return null;
        }

        console.log(
          `\n📦 Processing batch item ${itemIdx + 1}/${
            batchUpdates.length
          } (${wipItemId})`
        );
        console.log(`  Updates count: ${updates.length}`);

        // Get current item data to extract substageIds and stageIds
        const currentItem =
          batchItems.find(
            (item) => item.id === wipItemId || item._id === wipItemId
          ) ||
          trackerDetails.find(
            (item) => item.id === wipItemId || item._id === wipItemId
          );

        const nestedUpdatesMap = {}; // Group by stageKey + substageKey
        const simpleUpdates = []; // Simple field updates
        const substageIdMap = {}; // Map substageKey -> _id

        // First pass: collect substage IDs from current row data (not from updates)
        // The _id field is never edited, so we need to get it from the current row
        updates.forEach((update) => {
          const { stepField } = update;
          const parsed = parseFieldName(stepField);

          if (parsed.isNested && !parsed.isIdField) {
            // For each nested field, get the substageId from current row data
            // Use parsed.stageKey instead of update.stageKey to ensure correctness
            const { stageKey: parsedStageKey, substageKey } = parsed;
            const key = `${parsedStageKey}__${substageKey}`;

            // Only collect if we haven't already collected it
            if (!substageIdMap[key] && currentItem) {
              // Look up the _id field in current row: "stageKey: substageKey (_id)"
              const idFieldName = `${parsedStageKey}: ${substageKey} (_id)`;
              const substageId = currentItem[idFieldName];
              if (substageId) {
                substageIdMap[key] = substageId;
                console.log(
                  `  📌 Found substageId for ${key}: ${substageId} (from field: ${idFieldName})`
                );
              } else {
                console.warn(
                  `  ⚠️ substageId not found for ${key} (looked for field: ${idFieldName})`
                );
                // Debug: log available fields that match the pattern
                if (currentItem && typeof currentItem === "object") {
                  const matchingFields = Object.keys(currentItem).filter(
                    (k) =>
                      k.includes(parsedStageKey) &&
                      k.includes(substageKey) &&
                      k.includes("_id")
                  );
                  if (matchingFields.length > 0) {
                    console.log(`  🔍 Found similar fields:`, matchingFields);
                  }
                }
              }
            }
          }
        });

        // Second pass: process field updates
        updates.forEach((update, updateIdx) => {
          const { stageKey, stepField, value } = update;
          console.log(
            `  Update ${updateIdx + 1}/${
              updates.length
            }: ${stageKey} / ${stepField}`
          );

          // Parse the field name to determine structure (same logic as single item)
          const parsed = parseFieldName(stepField);

          // Skip ID fields (already collected in first pass)
          if (parsed.isIdField) {
            return;
          }

          if (!parsed.isNested) {
            // Simple field - send directly as stageUpdate
            console.log(`    ✅ Simple field`);
            simpleUpdates.push({
              stageKey,
              stepField,
              value,
            });
          } else {
            // Nested substage field - group by substageKey
            // Use parsed.stageKey instead of update.stageKey to ensure correctness
            const { stageKey: parsedStageKey, substageKey, field } = parsed;
            const key = `${parsedStageKey}__${substageKey}`;

            console.log(
              `    ✅ Nested: stageKey="${parsedStageKey}", substage="${substageKey}", field="${field}"`
            );

            if (!nestedUpdatesMap[key]) {
              nestedUpdatesMap[key] = {
                stageKey: parsedStageKey, // Use parsed stageKey, not from update object
                substageKey,
                substageId: substageIdMap[key] || null, // Include substageId if available
                updates: {},
              };
            }

            // Map field type to backend format (REQ -> req, ACTUAL -> actual, etc.)
            const backendField = mapFieldTypeToBackend(field);

            // Add the field to the nested substage update
            if (backendField === "comments") {
              // Comments are arrays of objects with userId
              if (!nestedUpdatesMap[key].updates.comments) {
                nestedUpdatesMap[key].updates.comments = [];
              }
              // Value is a single comment object { userId, createdAt, comment }
              if (Array.isArray(value)) {
                // If value is already an array, append all comments
                nestedUpdatesMap[key].updates.comments.push(...value);
              } else {
                // Single comment object
                nestedUpdatesMap[key].updates.comments.push(value);
              }
            } else {
              // Other fields (req, actual, control, status, etc.)
              let processedValue = value;

              // Convert status values to lowercase (API expects "approved", "rejected", "pending")
              if (backendField === "status" && typeof value === "string") {
                processedValue = value.toLowerCase();
              }

              // Convert Date objects to ISO strings for date fields
              if (
                (backendField === "req" ||
                  backendField === "actual" ||
                  backendField === "approvalDate") &&
                processedValue instanceof Date
              ) {
                processedValue = processedValue.toISOString();
              }

              nestedUpdatesMap[key].updates[backendField] = processedValue;
            }
          }
        });

        // Process nested updates: append rejection info to comments if STATUS is Rejected
        Object.values(nestedUpdatesMap).forEach((nestedUpdate) => {
          const statusValue = nestedUpdate.updates.status;
          const commentsValue = nestedUpdate.updates.comments;

          if (
            statusValue === "rejected" &&
            commentsValue &&
            Array.isArray(commentsValue)
          ) {
            // Get ACTUAL date - first check if it's in updates, otherwise get from current state
            let actualDate = nestedUpdate.updates.actual;

            // If ACTUAL is not in updates, try to get it from current batch items or tracker details
            if (!actualDate && currentItem) {
              // Find the field name for this substage's ACTUAL field
              const substageKey = nestedUpdate.substageKey;
              const actualFieldName = Object.keys(currentItem).find(
                (fieldName) =>
                  fieldName.includes(substageKey) &&
                  fieldName.endsWith("(ACTUAL)")
              );

              if (actualFieldName) {
                actualDate = currentItem[actualFieldName];
              }
            }

            let formattedActualDate = "";
            if (actualDate) {
              const date = new Date(actualDate);
              if (!isNaN(date.getTime())) {
                formattedActualDate = date.toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                });
              } else {
                formattedActualDate = actualDate;
              }
            }

            // Append rejection info to each comment
            if (formattedActualDate && nestedUpdate.updates.comments) {
              nestedUpdate.updates.comments = nestedUpdate.updates.comments.map(
                (commentObj) => {
                  if (
                    commentObj.comment &&
                    !commentObj.comment.includes("<Rejected | ACT:")
                  ) {
                    return {
                      ...commentObj,
                      comment: `${commentObj.comment} <Rejected | ACT: ${formattedActualDate}>`,
                    };
                  }
                  return commentObj;
                }
              );
            }
          }
        });

        // Build payload for this item - use new API format: substageUpdates
        const substageUpdates = Object.values(nestedUpdatesMap);
        const itemPayload = {};

        if (simpleUpdates.length > 0) {
          // Convert simple updates to stageUpdates format with stageId
          itemPayload.stageUpdates = simpleUpdates.map((update) => {
            // Get stageId from row data if available
            const stageIdField = `${update.stageKey}_id`;
            const stageId = currentItem?.[stageIdField] || null;

            return {
              stageId,
              stageKey: update.stageKey,
              updates: {
                [update.stepField]: update.value,
              },
            };
          });
          console.log(
            `  📦 stageUpdates (${itemPayload.stageUpdates.length}):`,
            itemPayload.stageUpdates
          );
        }

        if (substageUpdates.length > 0) {
          // Convert dates to ISO strings for substageUpdates
          substageUpdates.forEach((substageUpdate) => {
            Object.keys(substageUpdate.updates).forEach((key) => {
              const value = substageUpdate.updates[key];
              // Convert Date objects to ISO strings
              if (value instanceof Date) {
                substageUpdate.updates[key] = value.toISOString();
              } else if (
                typeof value === "string" &&
                (key === "req" || key === "actual" || key === "approvalDate") &&
                !value.includes("T")
              ) {
                // If it's a date string without time, ensure it's in ISO format
                const date = new Date(value);
                if (!isNaN(date.getTime())) {
                  substageUpdate.updates[key] = date.toISOString();
                }
              }
            });
          });

          itemPayload.substageUpdates = substageUpdates;
          console.log(
            `  📦 substageUpdates (${substageUpdates.length}):`,
            substageUpdates
          );
        }

        // Return in the format: { wipItemId, stageUpdates?, substageUpdates? }
        return {
          wipItemId,
          ...itemPayload,
        };
      })
      .filter(Boolean); // Remove null entries from invalid items

    console.log(
      "\n✅ Processed batch updates:",
      JSON.stringify(processedBatchUpdates, null, 2)
    );

    const payload = processedBatchUpdates;

    const headers = {
      "Content-Type": "application/json",
      Authorization: authToken,
    };

    try {
      const response = yield axios.put(requestURL, payload, { headers });

      console.log("Batch Response:", response);

      if (
        response &&
        response.status === 200 &&
        response.data &&
        response.data.success
      ) {
        console.log("INSIDE SUCCESS BLOCK:", response.data, batchUpdates);
        const totalUpdates = batchUpdates.reduce(
          (sum, item) => sum + (item.updates?.length || 0),
          0
        );
        yield put(
          setBulkUpdateWipDetailsSuccess({ updatedCount: totalUpdates })
        );
        // Don't show toast - component handles snackbar notifications

        // Refresh batch data if we're in batch view
        // This will be handled by the component after save
        return;
      } else {
        throw new Error(response.data?.message || "Batch update failed");
      }
    } catch (error) {
      console.error("Error in batch update:", error);
      yield put(
        setBulkUpdateWipDetailsError(error.message || "Failed to update items")
      );
      toast.error("Failed to update items");
      return;
    }
  }

  // SINGLE ITEM UPDATE
  console.log("🔄 SINGLE ITEM UPDATE MODE");
  console.log("wipItemId:", wipItemId);
  console.log("updates:", updates);
  console.log("viewParams:", viewParams);

  // Log comment updates specifically
  if (updates) {
    updates.forEach((update, idx) => {
      if (
        update.stepField &&
        update.stepField.toLowerCase().includes("comment")
      ) {
        console.log(`📝 Comment update #${idx + 1}:`, {
          field: update.stepField,
          valueType: Array.isArray(update.value)
            ? "array"
            : typeof update.value,
          value: update.value,
        });
      }
    });
  }

  if (!organisationId || !wipItemId) {
    console.warn("Missing required parameters, abandoning API call");
    yield put(
      setBulkUpdateWipDetailsError("Organisation ID and WIP Item ID required")
    );
    return;
  }

  const requestURL = API.WIP.UPDATE_WIP_ITEM.replace(
    "<ORGANISATION_ID>",
    organisationId
  ).replace("<WIP_ITEM_ID>", wipItemId);

  // Convert to backend format by parsing field names
  console.log("🔄 Converting updates to backend format...");

  // Get current item data to extract substageIds
  const currentItemForSubstageId =
    batchItems.find(
      (item) => item.id === wipItemId || item._id === wipItemId
    ) ||
    trackerDetails.find(
      (item) => item.id === wipItemId || item._id === wipItemId
    );

  const nestedUpdatesMap = {}; // Group by stageKey + substageKey
  const simpleUpdates = []; // Simple field updates
  const substageIdMap = {}; // Map substageKey -> _id

  // First pass: collect substage IDs from current row data (not from updates)
  // The _id field is never edited, so we need to get it from the current row
  updates.forEach((update) => {
    const { stepField, stageKey } = update;
    const parsed = parseFieldName(stepField);

    if (parsed.isNested && !parsed.isIdField) {
      // For each nested field, get the substageId from current row data
      const { substageKey } = parsed;
      const key = `${stageKey}__${substageKey}`;

      // Only collect if we haven't already collected it
      if (!substageIdMap[key] && currentItemForSubstageId) {
        // Look up the _id field in current row: "stageKey: substageKey (_id)"
        const idFieldName = `${stageKey}: ${substageKey} (_id)`;
        const substageId = currentItemForSubstageId[idFieldName];
        if (substageId) {
          substageIdMap[key] = substageId;
          console.log(
            `  📌 Found substageId for ${key}: ${substageId} (from field: ${idFieldName})`
          );
        } else {
          console.warn(
            `  ⚠️ substageId not found for ${key} (looked for field: ${idFieldName})`
          );
          // Debug: log available fields that match the pattern
          if (
            currentItemForSubstageId &&
            typeof currentItemForSubstageId === "object"
          ) {
            const matchingFields = Object.keys(currentItemForSubstageId).filter(
              (k) =>
                k.includes(stageKey) &&
                k.includes(substageKey) &&
                k.includes("_id")
            );
            if (matchingFields.length > 0) {
              console.log(`  🔍 Found similar fields:`, matchingFields);
            }
          }
        }
      }
    }
  });

  // Second pass: process field updates
  updates.forEach((update, idx) => {
    const { stageKey, stepField, value } = update;
    console.log(
      `  Update ${idx + 1}/${updates.length}: ${stageKey} / ${stepField}`
    );

    // Parse the field name to determine structure
    const parsed = parseFieldName(stepField);

    // Skip ID fields (already collected in first pass)
    if (parsed.isIdField) {
      return;
    }

    if (!parsed.isNested) {
      // Simple field - send directly as stageUpdate
      console.log(`    ✅ Simple field`);
      simpleUpdates.push({
        stageKey,
        stepField,
        value,
      });
    } else {
      // Nested substage field - group by substageKey
      const { substageKey, field } = parsed;
      const key = `${stageKey}__${substageKey}`;

      console.log(`    ✅ Nested: substage="${substageKey}", field="${field}"`);

      if (!nestedUpdatesMap[key]) {
        nestedUpdatesMap[key] = {
          stageKey,
          substageKey,
          substageId: substageIdMap[key] || null, // Include substageId if available
          updates: {},
        };
      }

      // Map field type to backend format (REQ -> req, ACTUAL -> actual, etc.)
      const backendField = mapFieldTypeToBackend(field);

      // Add the field to the nested substage update
      if (backendField === "comments") {
        // Comments are arrays of objects with userId
        if (!nestedUpdatesMap[key].updates.comments) {
          nestedUpdatesMap[key].updates.comments = [];
        }
        // Value is a single comment object { userId, createdAt, comment }
        if (Array.isArray(value)) {
          // If value is already an array, append all comments
          nestedUpdatesMap[key].updates.comments.push(...value);
        } else {
          // Single comment object
          nestedUpdatesMap[key].updates.comments.push(value);
        }
      } else {
        // Other fields (req, actual, control, status, etc.)
        let processedValue = value;

        // Convert status values to lowercase (API expects "approved", "rejected", "pending")
        if (backendField === "status" && typeof value === "string") {
          processedValue = value.toLowerCase();
        }

        // Convert Date objects to ISO strings for date fields
        if (
          (backendField === "req" ||
            backendField === "actual" ||
            backendField === "approvalDate") &&
          processedValue instanceof Date
        ) {
          processedValue = processedValue.toISOString();
        }

        nestedUpdatesMap[key].updates[backendField] = processedValue;
      }
    }
  });

  // Build final payload - use new API format: substageUpdates
  const substageUpdates = Object.values(nestedUpdatesMap);

  // Convert dates to ISO strings for substageUpdates
  substageUpdates.forEach((substageUpdate) => {
    Object.keys(substageUpdate.updates).forEach((key) => {
      const value = substageUpdate.updates[key];
      // Convert Date objects to ISO strings
      if (value instanceof Date) {
        substageUpdate.updates[key] = value.toISOString();
      } else if (
        typeof value === "string" &&
        (key === "req" || key === "actual" || key === "approvalDate") &&
        !value.includes("T")
      ) {
        // If it's a date string without time, ensure it's in ISO format
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          substageUpdate.updates[key] = date.toISOString();
        }
      }
    });
  });

  // Get current item data to extract stageIds for simple updates
  const currentItem =
    batchItems.find(
      (item) => item.id === wipItemId || item._id === wipItemId
    ) ||
    trackerDetails.find(
      (item) => item.id === wipItemId || item._id === wipItemId
    );

  const payload = {};

  if (simpleUpdates.length > 0) {
    // Convert simple updates to stageUpdates format with stageId
    payload.stageUpdates = simpleUpdates.map((update) => {
      // Get stageId from row data if available
      const stageIdField = `${update.stageKey}_id`;
      const stageId = currentItem?.[stageIdField] || null;

      return {
        stageId,
        stageKey: update.stageKey,
        updates: {
          [update.stepField]: update.value,
        },
      };
    });
    console.log(
      `📦 stageUpdates (${payload.stageUpdates.length}):`,
      payload.stageUpdates
    );
  }

  if (substageUpdates.length > 0) {
    payload.substageUpdates = substageUpdates;
    console.log(
      `📦 substageUpdates (${substageUpdates.length}):`,
      substageUpdates
    );
  }

  console.log("✅ Final payload:", JSON.stringify(payload, null, 2));

  const headers = {
    "Content-Type": "application/json",
    Authorization: authToken,
  };

  try {
    // Send updates in new API format
    // Backend expects: { substageUpdates: [...], stageUpdates: [...] }
    console.log("Sending PUT request with new API format...");
    const response = yield axios.put(requestURL, payload, { headers });

    console.log("Response:", response);

    if (
      response &&
      response.status === 200 &&
      response.data &&
      response.data.success
    ) {
      yield put(
        setBulkUpdateWipDetailsSuccess({ updatedCount: updates.length })
      );
      // Don't show toast - component handles snackbar notifications

      console.log("✅ Save successful, using optimistic update");
      console.log("Backend response data:", response.data.results);
      console.log("Original updates sent:", updates);

      // OPTIMISTIC UPDATE: Merge backend response with our sent updates
      // Backend may not return complete comment arrays, so we preserve our updates
      const updatedWipItem = response.data.results;

      if (updatedWipItem && updatedWipItem._id) {
        // Get current tracker details from Redux
        const currentTrackerDetails = yield select(
          (state) => state.wip.trackerDetails
        );

        console.log(
          "Current tracker details count:",
          currentTrackerDetails?.length
        );

        if (currentTrackerDetails && currentTrackerDetails.length > 0) {
          // Find and update the specific item
          const updatedDetails = currentTrackerDetails.map((row) => {
            if (
              row._id === updatedWipItem._id ||
              row.id === updatedWipItem._id
            ) {
              console.log(
                "✅ Updating row with complete backend response:",
                row._id
              );

              // Start with fresh object using backend response
              const flatRow = {
                id: updatedWipItem._id,
                _id: updatedWipItem._id,

                // Basic wipItem fields from backend response (handle object vs string)
                factoryName:
                  updatedWipItem.factoryId?.name || updatedWipItem.factoryName,
                factory:
                  updatedWipItem.factoryId?.name || updatedWipItem.factoryName,
                supplierName:
                  updatedWipItem.supplierId?.name ||
                  updatedWipItem.supplierName,
                styleNumber: updatedWipItem.styleNumber,
                vendorStyle: updatedWipItem.styleNumber,
                rootsStyle: updatedWipItem.styleNumber,
                po: updatedWipItem.poNumber,
                poNumber: updatedWipItem.poNumber,
                colour: updatedWipItem.colour,
                customer: updatedWipItem.customer,
                description: updatedWipItem.description,
                units: updatedWipItem.units,
                season: updatedWipItem.season,
                category: updatedWipItem.category || "",
                department: updatedWipItem.department || "",
                factoryId: updatedWipItem.factoryId,

                // Status rollup from backend
                statusRollup: updatedWipItem.statusRollup,
              };

              // Flatten ALL stages from backend response (complete data!)
              // Ground-up implementation: ALL stages use substages
              if (
                updatedWipItem.stages &&
                Array.isArray(updatedWipItem.stages)
              ) {
                updatedWipItem.stages.forEach((stage) => {
                  // Add stage metadata with prefixed keys
                  flatRow[`${stage.stageKey}_status`] = stage.status;
                  flatRow[`${stage.stageKey}_daysLate`] = stage.daysLate || 0;
                  flatRow[`${stage.stageKey}_displayName`] = stage.displayName;
                  flatRow[`${stage.stageKey}_id`] = stage._id;

                  // Backend structure: stages can have either substages array or stepsInStage object
                  const hasSubstages =
                    stage.substages &&
                    Array.isArray(stage.substages) &&
                    stage.substages.length > 0;
                  const hasStepsInStage =
                    stage.stepsInStage &&
                    Object.keys(stage.stepsInStage).length > 0;

                  if (hasSubstages) {
                    // Complex stage with substages array
                    flattenSubstagesArray(stage, flatRow);
                  } else if (hasStepsInStage) {
                    // Simple stage with stepsInStage key-value pairs
                    flattenStepsInStage(stage, flatRow);
                  }
                });
              }

              console.log(
                "✅ Row updated with",
                Object.keys(flatRow).length,
                "fields"
              );
              return flatRow;
            }
            return row;
          });

          console.log("Updated details count:", updatedDetails.length);

          // Update Redux with the modified array
          yield put(
            setWipTrackerDetails({
              details: updatedDetails,
              tracker: currentTrackerDetails[0], // Keep existing tracker
              allTrackers: currentTrackerDetails, // Keep existing allTrackers
            })
          );

          console.log(
            "✅ Optimistic update complete - no backend refresh needed"
          );
        }
      }
    }
  } catch (error) {
    console.error("Failed to update WIP details:", error);
    console.error("Error response:", error.response);
    console.error("Error response data:", error.response?.data);
    const errorMessage =
      error.response?.data?.message || error.message || "Update failed";
    yield put(setBulkUpdateWipDetailsError(errorMessage));
    toast.error(errorMessage);
    if (error && error.response && error.response.status === 401) {
      yield put(logout());
    }
  }
}

// ========================================
// COMMENT WORKERS
// Comments are now handled through the same PUT endpoint
// with stageKey and stepField format, not separate API calls
// ========================================

function* getWipCommentsWorker(action) {
  // Comments are embedded in WIP items, fetched via getWipTrackerDetails
  // No separate API needed
  yield put(setWipComments([]));
}

function* addWipCommentWorker(action) {
  // Comments are saved via bulkUpdateWipDetails with stageKey/stepField
  // No separate API needed
  console.log("Comment will be saved via regular update flow");
}

function* resolveWipCommentWorker(action) {
  // Comments are saved via bulkUpdateWipDetails with stageKey/stepField
  // No separate API needed
  console.log("Comment resolution handled via regular update flow");
}

// ========================================
// DELETE TRACKER WORKER (Not implemented in backend yet)
// ========================================

function* deleteWipTrackerWorker(action) {
  // Delete tracker API not documented in the actual backend
  console.warn("Delete tracker API not documented in backend");
  toast.info("Delete feature is not yet available");
}

/**
 * Fetch WIP reports for the organisation
 * Replaces getBatchUpdateIdsWorker - now fetches full report objects instead of just IDs
 * For supplier-facing-platform: Always includes supplierId from login
 */
function* getWipReportsWorker(action) {
  const authToken = yield select(selectAuthToken);
  const organisationId = yield select(selectOrganisationId);
  const supplierId = yield select(selectSupplierId); // Always get supplierId from login
  const { season, limit = 10000, offset = 0 } = action.payload || {};

  console.log("=== getWipReportsWorker ===");
  console.log("organisationId:", organisationId);
  console.log("supplierId (from login):", supplierId);
  console.log("season:", season);

  if (!organisationId) {
    console.warn("No Organisation Id found, abandoning API call");
    yield put(setBatchError("Organisation ID is required"));
    return;
  }

  if (!supplierId) {
    console.warn("No Supplier Id found in login, abandoning API call");
    yield put(setBatchError("Supplier ID is required"));
    return;
  }

  const requestURL = API.WIP.GET_WIP_REPORTS.replace(
    "<ORGANISATION_ID>",
    organisationId
  );

  const headers = {
    "Content-Type": "application/json",
    Authorization: authToken,
  };

  const params = { limit, offset, supplierId }; // Always include supplierId
  if (season) params.season = season;

  try {
    const response = yield axios.get(requestURL, { headers, params });

    console.log("GET WIP Reports response:", response.status);
    console.log("WIP Reports data:", response.data);

    if (response.data.success) {
      const reports = response.data.results?.reports || [];
      console.log(`✅ Fetched ${reports.length} WIP reports`);
      yield put(setWipReports(reports));
    } else {
      console.error("API returned success: false");
      yield put(
        setBatchError(response.data.message || "Failed to fetch WIP reports")
      );
    }
  } catch (error) {
    console.error("Error fetching WIP reports:", error);
    if (error.response?.status === 401) {
      yield put(logout());
    } else {
      yield put(setBatchError(error.message || "Failed to fetch WIP reports"));
      toast.error("Failed to load WIP reports");
    }
  }
}

/**
 * Fetch a single WIP report by ID
 */
function* getWipReportByIdWorker(action) {
  const authToken = yield select(selectAuthToken);
  const organisationId = yield select(selectOrganisationId);
  const { wipReportId } = action.payload || {};

  if (!organisationId || !wipReportId) {
    console.warn("Missing required parameters");
    yield put(setBatchError("Organisation ID and WIP Report ID are required"));
    return;
  }

  const requestURL = API.WIP.GET_WIP_REPORT_BY_ID.replace(
    "<ORGANISATION_ID>",
    organisationId
  ).replace("<WIP_REPORT_ID>", wipReportId);

  const headers = {
    "Content-Type": "application/json",
    Authorization: authToken,
  };

  try {
    const response = yield axios.get(requestURL, { headers });

    console.log("GET WIP Report by ID response:", response.status);
    console.log("WIP Report data:", response.data);

    if (response.data.success) {
      const report = response.data.results?.report || response.data.results;
      console.log(`✅ Fetched WIP report: ${report._id}`);
      yield put(setWipReportById(report));
    } else {
      console.error("API returned success: false");
      yield put(
        setBatchError(response.data.message || "Failed to fetch WIP report")
      );
    }
  } catch (error) {
    console.error("Error fetching WIP report:", error);
    if (error.response?.status === 401) {
      yield put(logout());
    } else {
      yield put(setBatchError(error.message || "Failed to fetch WIP report"));
      toast.error("Failed to load WIP report");
    }
  }
}

/**
 * Fetch all unique batch update IDs for the organisation (DEPRECATED - backward compatibility)
 */
function* getBatchUpdateIdsWorker(action) {
  // Delegate to getWipReportsWorker and extract IDs
  yield* getWipReportsWorker(action);
}

/**
 * Fetch WIP items by batch update ID (wipId = report _id)
 * For supplier-facing-platform: Always includes supplierId from login
 */
function* getWipItemsByBatchWorker(action) {
  const authToken = yield select(selectAuthToken);
  const organisationId = yield select(selectOrganisationId);
  const supplierId = yield select(selectSupplierId); // Always get supplierId from login
  const { batchUpdateId, limit = 10000, offset = 0 } = action.payload || {};

  console.log("=== getWipItemsByBatchWorker ===");
  console.log("batchUpdateId:", batchUpdateId);
  console.log("limit:", limit);
  console.log("offset:", offset);
  console.log("supplierId (from login):", supplierId);

  if (!organisationId || !batchUpdateId) {
    console.warn("Missing required parameters");
    yield put(
      setBatchError("Organisation ID and Batch Update ID are required")
    );
    return;
  }

  const requestURL = API.WIP.GET_ITEMS_BY_REPORT_ID.replace(
    "<ORGANISATION_ID>",
    organisationId
  ).replace("<WIP_REPORT_ID>", batchUpdateId);

  // Add pagination and supplierId query params
  const queryParams = [`limit=${limit}`, `offset=${offset}`];
  if (supplierId) {
    queryParams.push(`supplierId=${supplierId}`);
  }
  const url = `${requestURL}?${queryParams.join("&")}`;

  const headers = {
    "Content-Type": "application/json",
    Authorization: authToken,
  };

  try {
    const response = yield axios.get(url, { headers });

    if (response.data.success) {
      const wipItems = response.data.results.wipItems || [];
      const pagination = response.data.results.pagination || {
        total: 0,
        limit: 100,
        offset: 0,
        hasMore: false,
      };
      const lockedState = response.data.results.lockedState || {
        state: false,
        lockedByUserId: null,
        lockedByUserName: null,
        lockedAt: null,
      };

      console.log(
        `✅ Fetched ${wipItems.length} WIP items for batch ${batchUpdateId}`
      );

      // Transform each wipItem into ONE row (same as getWipTrackerDetailsWorker)
      // Flatten all stages into a single row object
      const transformedItems = wipItems
        .map((wipItem) => {
          if (!wipItem || !wipItem._id) {
            console.error("Invalid wipItem:", wipItem);
            return null;
          }

          const flatRow = {
            id: wipItem._id, // DataGrid requires 'id' field
            _id: wipItem._id,

            // Basic wipItem fields (handle object vs string for factoryId/supplierId)
            factoryName: wipItem.factoryId?.name || wipItem.factoryName,
            factory: wipItem.factoryId?.name || wipItem.factoryName,
            supplierName: wipItem.supplierId?.name || wipItem.supplierName,
            styleNumber: wipItem.styleNumber,
            vendorStyle: wipItem.styleNumber,
            rootsStyle: wipItem.styleNumber,
            po: wipItem.poNumber,
            poNumber: wipItem.poNumber,
            colour: wipItem.colour,
            customer: wipItem.customer,
            description: wipItem.description,
            units: wipItem.units,
            season: wipItem.season,
            category: wipItem.category || "",
            department: wipItem.department || "",

            // Store statusRollup for row-level status indicators
            statusRollup: wipItem.statusRollup,
          };

          // Standard stage order: main_information first, then material, then tc, then others
          const STANDARD_STAGE_ORDER = [
            "main_information",
            "material_specification",
            "tc_certificate",
            "fabric_approval",
            "trim_approval",
            "sample_status",
            "testing",
            "production",
            "inspection",
            "logistics",
          ];

          // Flatten ALL stages into this single row
          // Process stages in STANDARD_STAGE_ORDER to maintain consistent column order
          if (wipItem.stages && Array.isArray(wipItem.stages)) {
            // Create a map of stages by stageKey for quick lookup
            const stagesMap = new Map();
            wipItem.stages.forEach((stage) => {
              stagesMap.set(stage.stageKey, stage);
            });

            // Process stages in STANDARD_STAGE_ORDER first
            STANDARD_STAGE_ORDER.forEach((stageKey) => {
              const stage = stagesMap.get(stageKey);
              if (stage) {
                // Add stage metadata with prefixed keys to avoid conflicts
                flatRow[`${stage.stageKey}_status`] = stage.status;
                flatRow[`${stage.stageKey}_daysLate`] = stage.daysLate || 0;
                flatRow[`${stage.stageKey}_displayName`] = stage.displayName;
                flatRow[`${stage.stageKey}_id`] = stage._id; // Store stage ID for updates

                const hasSubstages =
                  stage.substages &&
                  Array.isArray(stage.substages) &&
                  stage.substages.length > 0;
                const hasStepsInStage =
                  stage.stepsInStage &&
                  Object.keys(stage.stepsInStage).length > 0;

                if (hasSubstages) {
                  // Complex stage with substages array
                  flattenSubstagesArray(stage, flatRow);
                } else if (hasStepsInStage) {
                  // Simple stage with stepsInStage key-value pairs
                  flattenStepsInStage(stage, flatRow);
                }

                // Remove from map so we know it's been processed
                stagesMap.delete(stageKey);
              }
            });

            // Process any remaining stages not in STANDARD_STAGE_ORDER (maintain backend order)
            stagesMap.forEach((stage) => {
              // Add stage metadata with prefixed keys to avoid conflicts
              flatRow[`${stage.stageKey}_status`] = stage.status;
              flatRow[`${stage.stageKey}_daysLate`] = stage.daysLate || 0;
              flatRow[`${stage.stageKey}_displayName`] = stage.displayName;
              flatRow[`${stage.stageKey}_id`] = stage._id; // Store stage ID for updates

              const hasSubstages =
                stage.substages &&
                Array.isArray(stage.substages) &&
                stage.substages.length > 0;
              const hasStepsInStage =
                stage.stepsInStage &&
                Object.keys(stage.stepsInStage).length > 0;

              if (hasSubstages) {
                // Complex stage with substages array
                flattenSubstagesArray(stage, flatRow);
              } else if (hasStepsInStage) {
                // Simple stage with stepsInStage key-value pairs
                flattenStepsInStage(stage, flatRow);
              }
            });
          }

          return flatRow;
        })
        .filter(Boolean); // Remove null entries

      console.log(
        `✅ Transformed ${wipItems.length} WIP items into ${transformedItems.length} flat rows for batch view`
      );

      yield put(
        setWipItemsByBatch({
          items: transformedItems,
          pagination,
          lockedState,
        })
      );
      yield put(setLockedState(lockedState));
    } else {
      console.error("API returned success: false");
      yield put(
        setBatchError(response.data.message || "Failed to fetch batch items")
      );
    }
  } catch (error) {
    console.error("Error fetching WIP items by batch:", error);
    if (error.response?.status === 401) {
      yield put(logout());
    } else {
      yield put(setBatchError(error.message || "Failed to fetch batch items"));
      toast.error("Failed to load WIP items for this batch");
    }
  }
}

function* watchWipSaga() {
  yield takeLatest(getWipItems.type, getWipItemsWorker);
  yield takeLatest(uploadWipExcel.type, uploadWipExcelWorker);
  yield takeLatest(getWipTrackers.type, getWipTrackersWorker);
  yield takeLatest(getWipTrackerDetails.type, getWipTrackerDetailsWorker);
  yield takeLatest(bulkUpdateWipDetails.type, bulkUpdateWipDetailsWorker);
  yield takeLatest(getWipComments.type, getWipCommentsWorker);
  yield takeLatest(addWipComment.type, addWipCommentWorker);
  yield takeLatest(resolveWipComment.type, resolveWipCommentWorker);
  yield takeLatest(deleteWipTracker.type, deleteWipTrackerWorker);
  // WIP Reports saga listeners (replaces batch update IDs)
  yield takeLatest(getWipReports.type, getWipReportsWorker);
  yield takeLatest(getWipReportById.type, getWipReportByIdWorker);
  // Batch update saga listeners (backward compatibility)
  yield takeLatest(getBatchUpdateIds.type, getBatchUpdateIdsWorker);
  yield takeLatest(getWipItemsByBatch.type, getWipItemsByBatchWorker);
  // Past-due saga listener
  yield takeLatest(getPastDueItems.type, getPastDueItemsWorker);
}

function* getPastDueItemsWorker(action) {
  const authToken = yield select(selectAuthToken);

  if (!authToken) {
    console.warn("No auth token found, abandoning API call");
    yield put(setPastDueError("Authentication token not found"));
    return;
  }

  const requestURL = API.WIP.GET_PAST_DUE;

  const headers = {
    "Content-Type": "application/json",
    Authorization: authToken,
  };

  try {
    const response = yield axios.get(requestURL, { headers });

    if (response && response.status === 200 && response.data) {
      if (response.data.success) {
        const pastDueItems = response.data.results || [];
        yield put(setPastDueItems(pastDueItems));
      } else {
        yield put(
          setPastDueError(
            response.data.message || "Failed to fetch past-due items"
          )
        );
      }
    }
  } catch (error) {
    console.error("Error fetching past-due items:", error);
    if (error.response?.status === 401) {
      yield put(logout());
    } else {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch past-due items";
      yield put(setPastDueError(errorMessage));
    }
  }
}

export default function* wipSaga() {
  yield all([watchWipSaga()]);
}
